import { useState } from 'react';
import { DEFAULT_LANG } from '../constants/all-language';

interface ILanguage {
  label: string;
  code: string;
  direction: string;
}

// Configuration for fallback behavior
interface FallbackConfig {
  fallbackOrder?: string[];
  enableConsoleWarnings?: boolean;
  retryOnError?: boolean;
  maxRetries?: number;
}

// Updated fallback order with English first
const DEFAULT_FALLBACK_CONFIG: Required<FallbackConfig> = {
  fallbackOrder: ['en', 'en-US', DEFAULT_LANG], // English first
  enableConsoleWarnings: true,
  retryOnError: false,
  maxRetries: 3,
};

export const useLanguageFallback = (config: FallbackConfig = {}) => {
  const finalConfig = { ...DEFAULT_FALLBACK_CONFIG, ...config };
  const [retryCount, setRetryCount] = useState(0);

  // Create English fallback language object
  const createEnglishFallback = (): ILanguage => ({
    code: 'en',
    label: 'English',
    direction: 'ltr'
  });

  // Find the best available language with fallback logic
  const findBestAvailableLanguage = (
    languages: ILanguage[], 
    preferredCode?: string
  ): ILanguage | null => {
    if (!languages || languages.length === 0) {
      if (finalConfig.enableConsoleWarnings) {
        console.warn('No languages available for fallback selection, using English');
      }
      return createEnglishFallback();
    }

    // First try the preferred language if provided
    if (preferredCode) {
      const preferred = languages.find(lang => lang.code === preferredCode);
      if (preferred) return preferred;
      
      if (finalConfig.enableConsoleWarnings) {
        console.warn(`Preferred language "${preferredCode}" not found, trying fallbacks`);
      }
    }

    // Try fallback languages in order (English first)
    for (const fallbackCode of finalConfig.fallbackOrder) {
      const fallback = languages.find(lang => lang.code === fallbackCode);
      if (fallback) {
        if (finalConfig.enableConsoleWarnings && preferredCode && preferredCode !== fallbackCode) {
          console.warn(`Using fallback language: "${fallbackCode}" instead of "${preferredCode}"`);
        }
        return fallback;
      }
    }

    // If no fallbacks found, return English or first available language
    const englishLang = languages.find(lang => lang.code === 'en');
    if (englishLang) {
      if (finalConfig.enableConsoleWarnings) {
        console.warn('Using English as ultimate fallback');
      }
      return englishLang;
    }

    // Last resort - return first available language
    if (finalConfig.enableConsoleWarnings) {
      console.warn('No English fallback found, using first available language');
    }
    return languages[0] || createEnglishFallback();
  };

  // Create a fallback language object when API fails - always English
  const createEmergencyFallback = (): ILanguage => {
    return createEnglishFallback();
  };

  // Validate if a language code exists in the available languages
  const validateLanguageCode = (languages: ILanguage[], code: string): boolean => {
    return languages.some(lang => lang.code === code);
  };

  // Get saved language from localStorage with validation
  const getSavedLanguage = (languages: ILanguage[]): string | null => {
    const saved = localStorage.getItem('_language');
    if (!saved) return null;
    
    // Validate saved language exists in available languages
    if (validateLanguageCode(languages, saved)) {
      return saved;
    }
    
    if (finalConfig.enableConsoleWarnings) {
      console.warn(`Saved language "${saved}" is no longer available, will use English fallback`);
    }
    return null;
  };

  // Ensure English is always available in languages array
  const ensureEnglishAvailable = (languages: ILanguage[]): ILanguage[] => {
    const hasEnglish = languages.some(lang => lang.code === 'en');
    if (!hasEnglish) {
      return [createEnglishFallback(), ...languages];
    }
    return languages;
  };

  // Retry mechanism for failed language fetches
  const shouldRetry = (): boolean => {
    return finalConfig.retryOnError && retryCount < finalConfig.maxRetries;
  };

  const incrementRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const resetRetry = () => {
    setRetryCount(0);
  };

  return {
    findBestAvailableLanguage,
    createEmergencyFallback,
    createEnglishFallback,
    validateLanguageCode,
    getSavedLanguage,
    ensureEnglishAvailable,
    shouldRetry,
    incrementRetry,
    resetRetry,
    retryCount,
    config: finalConfig,
  };
};

// Additional utility hook for language state management
export const useLanguageState = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const markInitialized = () => setIsInitialized(true);
  const setError = (error: string) => {
    setHasError(true);
    setErrorMessage(error);
  };
  const clearError = () => {
    setHasError(false);
    setErrorMessage(null);
  };

  return {
    isInitialized,
    hasError,
    errorMessage,
    markInitialized,
    setError,
    clearError,
  };
};

export default {
  useLanguageFallback,
  useLanguageState,
};

export type { ILanguage, FallbackConfig };

